/* =========================================================================
   LAYER'D Toolkit — installed-app behaviors (PWA Phase 2)
   File: assets/toolkit-app.js · loaded by learn-subnav.liquid (defer)

   Charter: everything the INSTALLED app does beyond a browser tab —
   tab-bar behavior, resume-where-you-left-off, app badge, wake lock,
   shortcut analytics. The install banner stays in toolkit-pwa.js
   (which early-returns inside the app, by design). Shell only: no
   tool logic, no data model changes, golden fixtures untouched.

   Every module fails silent (suite rule): a thrown API, a missing
   element, or private-mode storage must never affect a tool.
   ========================================================================= */
(function () {
  'use strict';

  var RESUME_KEY = 'layerd_app_resume';
  var RESUME_SCROLL_KEY = 'layerd_app_resume_scroll';   /* sessionStorage handoff */
  var WAKE_KEY = 'ldtl:wake';
  var RESUME_MAX_AGE_MS = 12 * 60 * 60 * 1000;          /* brief §7.2 */
  /* 2026-08-17 handle rename: /pages/planner + /pages/toolkit are the
     live paths; the old learn-hub/learn-timeline entries stay so a
     resume saved before the rename still validates. */
  var KNOWN_PATHS = ['/pages/toolkit', '/pages/studio', '/pages/planner',
    '/pages/learn-hub', '/pages/learn-timeline',
    '/pages/serving-size-calculator', '/pages/pricing', '/pages/structure',
    '/pages/recipe-library', '/pages/batch-math', '/pages/cake-er',
    '/pages/pantry', '/pages/bloom-atlas', '/pages/dashboard'];

  var isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;
  var isIOS = /iP(hone|od|ad)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); return true; } catch (e) { return false; } }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (e) { } }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { } }
  function ssDel(k) { try { sessionStorage.removeItem(k); } catch (e) { } }
  function parse(raw) { try { return JSON.parse(raw); } catch (e) { return null; } }

  function tier() {
    var el = document.querySelector('[data-lh-tier]');
    return (el && el.getAttribute('data-lh-tier')) || 'unknown';
  }
  function track(eventName, extra) {
    try {
      var params = { platform: isIOS ? 'ios' : 'android', user_tier: tier() };
      for (var k in (extra || {})) { params[k] = extra[k]; }
      if (typeof window.lhTrack === 'function') window.lhTrack(eventName, params);
      else if (typeof window.gtag === 'function') window.gtag('event', eventName, params);
    } catch (e) { }
  }

  var q = {};
  try {
    location.search.replace(/^\?/, '').split('&').forEach(function (p) {
      var kv = p.split('=');
      if (kv[0]) q[decodeURIComponent(kv[0])] = decodeURIComponent(kv[1] || '');
    });
  } catch (e) { }

  /* ---------- boot flags ------------------------------------------------ */
  if (isStandalone) {
    document.documentElement.classList.add('ld-standalone');
    /* once per session: the "app-installed-session" GA4 label */
    if (!ssGet('layerd_app_session')) {
      ssSet('layerd_app_session', '1');
      track('toolkit_pwa_app_session');
    }
  }
  if (q.source === 'shortcut') track('toolkit_pwa_shortcut_launch', { view: q.ldview || 'cake-er' });

  /* ---------- tool last-opened ledger (member dashboard) ----------------- */
  /* One write here covers every tool page — this file loads suite-wide via
     the subnav section. The dashboard reads the map (read-only) for its
     recently-used sort; browser AND standalone visits both count. */
  var OPENED_KEY = 'layerd-tool-last-opened';
  var TOOL_SLUGS = {
    '/pages/studio': 'studio',
    '/pages/planner': 'planner',
    '/pages/learn-timeline': 'planner',   /* pre-rename path, kept */
    '/pages/serving-size-calculator': 'serving',
    '/pages/pricing': 'pricing',
    '/pages/structure': 'structure',
    '/pages/recipe-library': 'recipes',
    '/pages/batch-math': 'batch-math',
    '/pages/cake-er': 'cake-er',
    '/pages/pantry': 'pantry',
    '/pages/bloom-atlas': 'bloom-atlas'
  };
  (function noteToolOpened() {
    var slug = TOOL_SLUGS[location.pathname.replace(/\/+$/, '')];
    if (!slug) return;
    var map = parse(lsGet(OPENED_KEY)) || {};
    map[slug] = Date.now();
    lsSet(OPENED_KEY, JSON.stringify(map));
  })();

  /* ---------- resume where you left off (standalone only, §7) ----------- */
  function todayKey(ts) {
    var d = ts ? new Date(ts) : new Date();
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }
  function saveResume() {
    if (!isStandalone) return;
    lsSet(RESUME_KEY, JSON.stringify({
      path: location.pathname + location.search + location.hash,
      scrollY: window.pageYOffset || 0,
      activeBookingId: lsGet('layerd-learn-active-booking'),
      at: Date.now()
    }));
  }
  function tryResume() {
    if (!isStandalone) return;
    /* round 38 (Mariya: "everything I click redirects to Planner"):
       resume fires ONCE per app session - a COLD LAUNCH only. Before
       this latch it hijacked every in-app navigation: pagehide saved
       the page being LEFT (usually the Planner), the tapped page
       loaded, and resume immediately replaced it with the save. Tab
       taps, the pipebar's Next, every hand-off - all bounced back. */
    var resumed = ssGet('layerd_app_resumed');
    ssSet('layerd_app_resumed', '1');
    if (resumed) return;
    /* explicit entry points always win (§7.3) - a scoped ?booking
       hand-off is explicit intent too. UX 2026-09-09: ?ldsp=tools /
       ?ldsp=bookings (the Studio bottom-bar tabs) are deliberate too. (The
       param was renamed from `view` — that's a reserved Shopify parameter.) */
    if (q.source === 'shortcut' || q.ldview || q.file || q.booking || q.ldsp) return;
    var r = parse(lsGet(RESUME_KEY));
    if (!r || !r.path || typeof r.at !== 'number') return;
    var fresh = (Date.now() - r.at) < RESUME_MAX_AGE_MS && todayKey(r.at) === todayKey();
    if (!fresh) { lsDel(RESUME_KEY); return; }        /* stale -> stay on start_url */
    var purePath = r.path.split('?')[0].split('#')[0];
    /* UX 2026-09-09: resume ONLY ever returns to the Studio workspace. It used
       to accept any KNOWN_PATHS tool page, and a stale resume pointing at an old
       cached tool page (the retired "will this cake survive?" Structure shell)
       trapped every re-entry there. Anything else is discarded and the cold
       launch simply lands on start_url (the studio). */
    if (purePath !== '/pages/studio') { lsDel(RESUME_KEY); return; }
    var here = location.pathname + location.search + location.hash;
    if (r.path === here || purePath === location.pathname) {
      restoreScroll(r.scrollY);                        /* already there — just scroll */
      return;
    }
    lsDel(RESUME_KEY);                                 /* consume BEFORE replace: no loops */
    ssSet(RESUME_SCROLL_KEY, String(r.scrollY || 0));
    track('toolkit_pwa_resume', { age_min: Math.round((Date.now() - r.at) / 60000) });
    location.replace(r.path);
  }
  function restoreScroll(y) {
    y = +y || 0;
    if (y < 1) return;
    /* tools render async — retry until the page is tall enough or we give up */
    var tries = 0;
    (function attempt() {
      if (document.documentElement.scrollHeight >= y + window.innerHeight * 0.5) {
        window.scrollTo(0, y); return;
      }
      if (++tries < 10) setTimeout(attempt, 300);
    })();
  }
  var handedScroll = ssGet(RESUME_SCROLL_KEY);
  if (handedScroll !== null) { ssDel(RESUME_SCROLL_KEY); restoreScroll(handedScroll); }
  window.addEventListener('pagehide', saveResume);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') saveResume();
  });

  /* ---------- bottom tab bar behavior (§2) ------------------------------ */
  function wireTabbar() {
    var bar = document.querySelector('.ld-tabbar');
    if (!bar) return;
    /* round 37: the Clients tab retired (Recipes took the slot) — the
       shared-page active-state swap went with it */
    bar.addEventListener('click', function (e) {
      var tab = e.target && e.target.closest && e.target.closest('.ld-tabbar__tab');
      if (!tab) return;
      track('toolkit_pwa_tab_tap', { tab: tab.getAttribute('data-tab') || '' });
      if (tab.classList.contains('is-active')) {       /* re-tap = back to top (§2.3) */
        e.preventDefault();
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        try { window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }); }
        catch (err) { window.scrollTo(0, 0); }
      }
    });
  }

  /* ---------- app badge — today's remaining Planner tasks (§6.3) -------- */
  function isoToday() {
    var t = new Date();
    return t.getFullYear() + '-' + ('0' + (t.getMonth() + 1)).slice(-2) + '-' + ('0' + t.getDate()).slice(-2);
  }
  function updateBadge() {
    if (!('setAppBadge' in navigator)) return;
    var digest = parse(lsGet('ldtl:digest'));
    var d = digest && digest[isoToday()];
    var remaining = d ? Math.max(0, (+d.all || 0) - (+d.done || 0)) : 0;
    try {
      if (remaining > 0) navigator.setAppBadge(remaining);
      else navigator.clearAppBadge();
    } catch (e) { }
  }
  /* ---------- r61: the badge explains itself at the door ----------------
     Her report (2026-08-20): "a red number ... and when I open the app it
     doesn't really tell me what I've missed." No notification inbox — that
     would be a second home for Planner facts, and second homes drift. One
     slim strip on the landing screens (hub + dashboard), computed from the
     SAME digest as the icon badge, so the red number and its explanation
     can never disagree. Finish the tasks and both clear together. */
  /* 2026-09-18 (Mariya): the strip vanished the moment she opened a tool page
     (Cake ER etc.) — it only lived on the landing screens, so it read as "the
     top task list disappears". Carry it across the native tool pages too so the
     "tasks still open today" reminder stays put as she moves through the toolkit.
     Each still only shows when there are open tasks and clears together. */
  var STRIP_PAGES = ['/pages/toolkit', '/pages/studio', '/pages/learn-hub', '/pages/dashboard',
    '/pages/cake-er', '/pages/structure', '/pages/batch-math',
    '/pages/serving-size-calculator', '/pages/pricing', '/pages/recipe-library', '/pages/planner'];
  var STRIP_DISMISS = 'ld-badgestrip-dismiss';   /* holds the iso it was dismissed for */
  function plannerUrl() {
    var tabs = document.querySelectorAll('.learn-subnav__tab');
    for (var i = 0; i < tabs.length; i++) {
      var h = tabs[i].getAttribute('href') || '';
      if (/planner|timeline/i.test(h)) return h;
    }
    return '/pages/planner';
  }
  /* 2026-09-18 (Mariya): on the desktop tool pages the shared shell (.lh-shell)
     wraps the tool's content column beside the sidebar, and each tool renders
     .learn-subnav INSIDE that column - so anchoring the strip to the subnav
     trapped it in the narrow content column (it stopped at the divider instead
     of spanning over the sidebar). Sit the band ABOVE the shell when one exists
     so it spans both columns, exactly like it does on the Studio. The shell is
     built synchronously at DOMContentLoaded by learn-components.js, which loads
     before this file, so it's already present the first time stripUpdate runs;
     re-home on later calls too, in case a resize builds the shell afterwards. */
  function placeStrip(host) {
    var shell = document.querySelector('.lh-shell');
    if (shell && shell.parentNode) {
      if (host.parentNode !== shell.parentNode || host.nextElementSibling !== shell) {
        shell.parentNode.insertBefore(host, shell);
      }
      return;
    }
    if (host.parentNode) return;   /* already placed (mobile / no shell) */
    var nav = document.querySelector('.learn-subnav');
    if (nav && nav.parentNode) nav.parentNode.insertBefore(host, nav.nextSibling);
    else document.body.insertBefore(host, document.body.firstChild);
  }
  function stripUpdate() {
    var onLanding = STRIP_PAGES.some(function (p) { return location.pathname.indexOf(p) === 0; });
    if (!onLanding && q.stripdemo !== '1') return;   /* ?stripdemo=1 = preview QA door */
    var host = document.getElementById('ld-badgestrip');
    var digest = parse(lsGet('ldtl:digest'));
    var d = digest && digest[isoToday()];
    var remaining = d ? Math.max(0, (+d.all || 0) - (+d.done || 0)) : 0;
    if (!remaining || lsGet(STRIP_DISMISS) === isoToday()) {
      if (host) host.remove();
      return;
    }
    /* 2026-09-18 (Mariya): keep the strip to just the count + the door - drop
       the "- <client> · <task>" detail. It read as noise next to the number. */
    var line = '<span class="ld-badgestrip__dot" aria-hidden="true"></span>' +
      '<span class="ld-badgestrip__t"><strong>' + remaining + (remaining === 1 ? ' task' : ' tasks') +
      ' still open today</strong></span>' +
      '<a class="ld-badgestrip__go" href="' + plannerUrl() + '">See today →</a>' +
      '<button type="button" class="ld-badgestrip__x" aria-label="Hide for today">×</button>';
    if (!host) {
      host = document.createElement('div');
      host.id = 'ld-badgestrip';
      host.className = 'ld-badgestrip';
      host.setAttribute('role', 'status');
      placeStrip(host);
      host.addEventListener('click', function (ev) {
        if (ev.target && ev.target.className === 'ld-badgestrip__x') {
          lsSet(STRIP_DISMISS, isoToday());
          host.remove();
        }
      });
      track('toolkit_badge_strip_shown', { remaining: remaining });
    } else {
      placeStrip(host);   /* re-home if the desktop shell mounted after the strip */
    }
    host.innerHTML = line;
  }
  /* scoped styles ride the shell JS so the strip ships in ONE file —
     tokens with fallbacks, hairline border, no shadows (house rules) */
  (function stripCss() {
    var s = document.createElement('style');
    s.textContent =
      '.ld-badgestrip{display:flex;align-items:center;gap:10px;max-width:960px;margin:10px auto 0;' +
      'padding:10px 14px;width:calc(100% - 24px);box-sizing:border-box;' +
      'background:var(--layerd-card,#fff);border:1px solid var(--hairline,rgba(42,33,56,.12));border-radius:10px;' +
      'font-family:"Hanken Grotesk","Jost",sans-serif;font-size:13.5px;color:var(--plum,#2A2138);}' +
      '.ld-badgestrip__dot{flex:none;width:8px;height:8px;border-radius:50%;background:var(--ld-wine,#8E4A47);}' +
      '.ld-badgestrip__t{flex:1 1 auto;min-width:0;}' +
      '.ld-badgestrip__go{flex:none;color:var(--plum,#2A2138);font-weight:600;text-decoration:underline;text-underline-offset:2px;}' +
      '.ld-badgestrip__x{flex:none;border:0;background:none;cursor:pointer;font-size:16px;line-height:1;' +
      'color:var(--ink-40,rgba(42,33,56,.4));padding:6px;margin:-6px;}';
    document.head.appendChild(s);
  })();

  window.addEventListener('storage', function (e) {
    if (e && e.key && e.key.indexOf('ldtl:') === 0) { updateBadge(); stripUpdate(); }
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') { updateBadge(); stripUpdate(); }
    else if (window.LAYERD && window.LAYERD.wake) window.LAYERD.wake.onHidden();
  });

  /* ---------- wake lock — LAYERD.wake (§6.1) ---------------------------- */
  var wake = {
    _sentinel: null,
    supported: !!(navigator.wakeLock && navigator.wakeLock.request),
    on: function () { return lsGet(WAKE_KEY) === '1'; },
    request: function () {
      if (!wake.supported) return;
      navigator.wakeLock.request('screen').then(function (s) {
        wake._sentinel = s;
        s.addEventListener('release', function () { wake._sentinel = null; });
      }).catch(function () { });
    },
    release: function () {
      if (wake._sentinel) { try { wake._sentinel.release(); } catch (e) { } wake._sentinel = null; }
    },
    toggle: function () {
      var next = !wake.on();
      lsSet(WAKE_KEY, next ? '1' : '0');
      if (next) { wake.request(); track('toolkit_pwa_wake_lock_on'); }
      else { wake.release(); track('toolkit_pwa_wake_lock_off'); }
      syncWakeChips();
      return next;
    },
    onVisible: function () { if (wake.on() && !wake._sentinel) wake.request(); },
    onHidden: function () { /* browser auto-releases; sentinel handler clears state */ }
  };
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') wake.onVisible();
  });
  window.addEventListener('pagehide', function () { wake.release(); });

  window.LAYERD = window.LAYERD || {};
  window.LAYERD.wake = wake;

  /* chip UI: injected so tool files stay untouched. Homes (in order of
     preference): the Planner's view-options row (.tl-viewrow — survives
     as a concept across re-renders even though the node is rebuilt), and
     the Cake ER card's footer action row. Re-injected via a debounced
     MutationObserver because both tools rebuild innerHTML wholesale. If wake
     lock is unsupported (iOS < 16.4) no chip renders — the feature is silent.
     2026-09-15 (Mariya): the Cake ER homes pointed at .cker-entry / __foot /
     article[data-entry], classes this page no longer renders, so the chip never
     appeared — on the ONE tool people open with buttercream on their hands. It
     targets the card footer's actions now. */
  function wakeChipHtml() {
    return '<button type="button" class="ld-chip ld-wakechip" aria-pressed="' +
      (wake.on() ? 'true' : 'false') + '">Keep screen awake' +
      (wake.on() ? ' ✓' : '') + '</button>';
  }
  function syncWakeChips() {
    var chips = document.querySelectorAll('.ld-wakechip');
    for (var i = 0; i < chips.length; i++) {
      chips[i].setAttribute('aria-pressed', wake.on() ? 'true' : 'false');
      chips[i].textContent = 'Keep screen awake' + (wake.on() ? ' ✓' : '');
    }
  }
  function injectWakeChips() {
    if (!wake.supported) return;
    var homes = document.querySelectorAll('.tl-viewrow, .cker-card__actions');
    for (var i = 0; i < homes.length; i++) {
      if (!homes[i].querySelector('.ld-wakechip')) {
        var span = document.createElement('span');
        span.innerHTML = wakeChipHtml();
        homes[i].appendChild(span.firstChild);
      }
      break;   /* one chip per page is enough — first home wins */
    }
  }
  document.addEventListener('click', function (e) {
    var chip = e.target && e.target.closest && e.target.closest('.ld-wakechip');
    if (chip) wake.toggle();
  });
  var injectTimer = null;
  function scheduleInject() {
    if (injectTimer) return;
    injectTimer = setTimeout(function () { injectTimer = null; injectWakeChips(); }, 400);
  }
  if (wake.supported && window.MutationObserver) {
    new MutationObserver(scheduleInject)
      .observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- go ---------------------------------------------------------- */
  function init() {
    tryResume();
    wireTabbar();
    updateBadge();
    stripUpdate();   /* r61: the strip boots with the badge it explains */
    injectWakeChips();
    if (wake.on()) wake.request();   /* remembered within the session */
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
